// filepath: /Users/austin/workspace/docuhounds/react-frontend/src/mobile/pages/MobileEventDetailsPage.js
// Added "Event Assignments" quick action button
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { EventApi, EndeavorApi } from '../../services/ApiService';
import EventDetailsSection from '../../components/details/EventDetailsSection';

export default function MobileEventDetailsPage(){
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { deleteEvent, deleteEventSeries } = useData();
  const [event,setEvent]=useState(null);
  const [endeavor,setEndeavor]=useState(null);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Check if this is a recurring event
  const isRecurringEvent = Boolean(
    event?.isRecurring || 
    event?.recurrenceRule || 
    event?.parentEventId
  );

  const formatDateTime = useCallback(dt=>{
    if(!dt) return 'Not set';
    const d = new Date(dt);
    if(isNaN(d)) return 'Invalid date';
    return d.toLocaleString('en-US',{
      month:'short',day:'numeric',year:'numeric',
      hour:'numeric',minute:'2-digit',hour12:true
    });
  },[]);

  const isPastEvent = useMemo(()=>{
    if(!event?.startDatetime) return false;
    return new Date(event.startDatetime) < new Date();
  },[event]);

  const eventInfo = useMemo(()=>{
    if(!event) return {};
    return {
      typeName: event.eventType || 'Event',
      start: formatDateTime(event.startDatetime),
      end: formatDateTime(event.endDatetime),
      duration: event.startDatetime && event.endDatetime
        ? `${formatDateTime(event.startDatetime)} - ${formatDateTime(event.endDatetime)}`
        : 'Time not set',
      status: isPastEvent ? 'Past' : 'Upcoming',
      createdAt: formatDateTime(event.createdAt),
      updatedAt: formatDateTime(event.updatedAt)
    };
  },[event,formatDateTime,isPastEvent]);

  useEffect(()=>{
    let active=true;
    (async()=>{
      try{
        setLoading(true);
        const eRes = await EventApi.getEvent(eventId);
        if(!eRes.ok) throw new Error('Failed to load event');
        const eData = await eRes.json();
        if(!active) return;
        setEvent(eData);
        if(eData.endeavorId){
          const cRes = await EndeavorApi.getEndeavor(eData.endeavorId);
          if(cRes.ok){
            const cData = await cRes.json();
            if(active) setEndeavor(cData);
          }
        }
      }catch(e){
        if(active) setErr(e.message);
      }finally{
        if(active) setLoading(false);
      }
    })();
    return ()=>{active=false;};
  },[eventId]);

  if(loading) return <div className="m-screen"><p>Loading event...</p></div>;
  if(err) return <div className="m-screen"><p>Error: {err}</p></div>;
  if(!event) return <div className="m-screen"><p>Not found.</p></div>;

  const handleDeleteSingle = async () => {
    try {
      await deleteEvent(eventId);
      setShowDeleteConfirm(false);
      navigate('/m/events');
    } catch (error) {
      setErr(error.message || 'Failed to delete event');
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteSeries = async () => {
    try {
      const parentId = event.parentEventId || event.eventId || event.id;
      const response = await deleteEventSeries(parentId);
      if (!response.ok) {
        throw new Error('Failed to delete series');
      }
      setShowDeleteConfirm(false);
      navigate('/m/events');
    } catch (error) {
      setErr(error.message || 'Failed to delete series');
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="m-screen">
      <div className="m-page-header">
        <button className="m-back-btn" onClick={()=>navigate('/m/events')}>← Events</button>
        <div className="m-page-title">Event</div>
        <button 
          className="m-delete-btn" 
          onClick={() => setShowDeleteConfirm(true)}
          title="Delete event"
        >
          🗑️
        </button>
      </div>
      <EventDetailsSection
        event={event}
        eventInfo={eventInfo}
        isPastEvent={isPastEvent}
        endeavor={endeavor}
        onEdit={()=>navigate(`/m/event/${eventId}/edit`)}
        onGoToEndeavor={()=>endeavor && navigate(`/m/course/${endeavor.endeavorId||endeavor.id}`)}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Delete Event{isRecurringEvent ? ' (Recurring)' : ''}?</h3>
            {isRecurringEvent ? (
              <>
                <p>This is a recurring event. What would you like to delete?</p>
                <div className="delete-confirmation-actions recurring-delete-options">
                  <button
                    className="btn-cancel"
                    onClick={() => setShowDeleteConfirm(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-delete-single"
                    onClick={handleDeleteSingle}
                    type="button"
                  >
                    Delete This Event Only
                  </button>
                  <button
                    className="btn-delete-series"
                    onClick={handleDeleteSeries}
                    type="button"
                  >
                    Delete Entire Series
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Are you sure you want to permanently delete this event?</p>
                <div className="delete-confirmation-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => setShowDeleteConfirm(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-delete"
                    onClick={handleDeleteSingle}
                    type="button"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Event Assignments removed for mobile - rely on bottom tab navigation */}
    </div>
  );
}